/* ============================================
   Horas / Plan — nivel cuenta (/portal/horas)
   El paquete de horas es POR CLIENTE (config_tarifa_cliente), no por proyecto.
   Muestra el saldo del mes y en qué se consumieron las horas.
   Config (tarifa/umbral/consumo) viene del backend: GET /api/portal/plan-horas.
   El desglose por proyecto se calcula en el cliente desde las tareas (RLS).
   ============================================ */

import { useMemo } from 'react'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useCachedData } from '@/hooks/useCachedData'
import { useApi } from '@/hooks/useApi'
import { supabase } from '@/lib/supabase'
import { formatCurrency } from '@/lib/utils'
import type { Tarea, Proyecto } from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'

interface PlanHoras {
  tiene_plan: boolean
  tarifa_hora?: number
  umbral_horas_mes?: number
  moneda?: string
  consumo_mes?: number
  periodo?: string
}

function mesActualLabel(): string {
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  const d = new Date()
  return `${meses[d.getMonth()]} ${d.getFullYear()}`
}

export default function PortalHorasPlan() {
  const { activeClienteId: clienteId } = usePortalCliente()
  const api = useApi()
  const { data: datos, isLoading: loading } = useCachedData(
    ['portal-horas-plan', clienteId],
    async () => {
      const [planRes, hosting] = await Promise.all([
        api.get<PlanHoras>(`/api/portal/plan-horas?cliente_id=${clienteId}`).catch(() => null),
        api.get<{ proyecto_id?: number | null }[]>(`/api/hosting/mi-hosting?cliente_id=${clienteId}`).catch(() => []),
      ])
      const hostingIds = new Set(
        (Array.isArray(hosting) ? hosting : []).map(h => h.proyecto_id).filter((v): v is number => v != null)
      )
      const { data: pData } = await supabase.from('proyectos').select('id, nombre').eq('cliente_id', clienteId)
      const proyectos = ((pData ?? []) as unknown as Proyecto[]).filter(p => !hostingIds.has(p.id))
      const ids = proyectos.map(p => p.id)
      const plan = planRes ?? { tiene_plan: false }
      if (ids.length === 0) return { plan, proyectos, tareas: [] as Tarea[] }
      const { data: tData } = await supabase.from('tareas').select('proyecto_id, horas_reales').in('proyecto_id', ids)
      return { plan, proyectos, tareas: (tData ?? []) as unknown as Tarea[] }
    },
    { activo: !!clienteId },
  )
  const plan = datos?.plan ?? null
  const proyectos = datos?.proyectos ?? []
  const tareas = datos?.tareas ?? []

  // Horas registradas por proyecto (todas las que llevan horas_reales).
  const desglose = useMemo(() => {
    const porProyecto = new Map<number, number>()
    tareas.forEach(t => {
      // Una tarea de oportunidad todavia no tiene proyecto: no suma horas de plan.
      if (t.proyecto_id != null && (t.horas_reales ?? 0) > 0) {
        porProyecto.set(t.proyecto_id, (porProyecto.get(t.proyecto_id) ?? 0) + t.horas_reales)
      }
    })
    return proyectos
      .map(p => ({ nombre: p.nombre, horas: porProyecto.get(p.id) ?? 0 }))
      .filter(r => r.horas > 0)
      .sort((a, b) => b.horas - a.horas)
  }, [proyectos, tareas])

  const totalRegistradas = useMemo(() => desglose.reduce((s, r) => s + r.horas, 0), [desglose])

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando plan de horas...</p>
      </div>
    )
  }

  // Sin plan: no debería llegar (el nav lo oculta), pero por si acceden por URL.
  if (!plan?.tiene_plan) {
    return (
      <div className="pt-fact-empty" style={{ padding: '48px 24px' }}>
        <h3>Sin paquete de horas</h3>
        <p>Tu cuenta no tiene un paquete mensual de horas configurado. Si crees que es un error, escríbenos a soporte.</p>
      </div>
    )
  }

  const umbral = plan.umbral_horas_mes ?? 0
  const tarifa = plan.tarifa_hora ?? 0
  const moneda = plan.moneda ?? 'USD'
  // Consumo del mes: el backend es la fuente de verdad; si no lo manda, caemos
  // al total de horas registradas como aproximación.
  const consumo = plan.consumo_mes ?? totalRegistradas
  const disponibles = Math.max(0, umbral - consumo)
  const extra = Math.max(0, consumo - umbral)
  const pct = umbral > 0 ? Math.min(100, (consumo / umbral) * 100) : 0

  return (
    <div className="pi-layout">
      <div className="pi-head">
        <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Horas / Plan' }]} meta={mesActualLabel()} />
      </div>

      {/* KPIs */}
      <div className="pi-kpis">
        <div className="pi-kpi">
          <span className="pi-kpi-label">Plan mensual</span>
          <span className="pi-kpi-val">{umbral}<small> h · mes</small></span>
        </div>
        <div className="pi-kpi">
          <span className="pi-kpi-label">Consumidas</span>
          <span className="pi-kpi-val">{consumo}<small> h</small></span>
        </div>
        <div className="pi-kpi">
          <span className="pi-kpi-label">Disponibles</span>
          <span className="pi-kpi-val">{disponibles}<small> h</small></span>
        </div>
        <div className="pi-kpi">
          <span className="pi-kpi-label">Extra este mes</span>
          <span className="pi-kpi-val">{extra > 0 ? formatCurrency(extra * tarifa) : formatCurrency(0)}</span>
        </div>
      </div>

      {/* Burn-down */}
      <div className="pd-card">
        <div className="pd-card-head">
          <h3>Consumo de {mesActualLabel()}</h3>
          <span className="pi-model pi-model--horas">{tarifa} {moneda}/h extra</span>
        </div>
        <div className="pd-card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <span className="pd-bill-figure">{consumo}<small> / {umbral} h</small></span>
            <span className="pi-mini">quedan <b>{disponibles} h</b></span>
          </div>
          <div className="pd-bill-bar" style={{ height: 14 }}>
            <i style={{ width: `${pct}%`, background: extra > 0 ? 'var(--danger, #d63031)' : undefined }} />
          </div>
          {extra > 0 && (
            <p className="pd-bill-note">Superaste el plan por <b>{extra} h</b>. Las horas extra se cobran a {tarifa} {moneda}/h en la factura del mes.</p>
          )}
        </div>
      </div>

      {/* Desglose */}
      <div className="pd-card">
        <div className="pd-card-head"><h3>¿En qué se fueron?</h3><span className="pi-mini">{totalRegistradas} h registradas</span></div>
        <div className="pd-card-body">
          {desglose.length === 0 ? (
            <p className="pd-desc" style={{ margin: 0 }}>Aún no hay horas registradas en tus proyectos.</p>
          ) : (
            <div className="pd-tasklist">
              {desglose.map((r, i) => (
                <div key={i} className="pd-trow" style={{ cursor: 'default' }}>
                  <span className="pd-trow-title">{r.nombre}</span>
                  <span className="pd-chip">{r.horas} h</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
